// ─── src/hooks/useMeetupProposals.ts ─────────────────────────────────────────

import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import {
  approveMeetupProposal,
  deleteMeetupProposal,
  getMeetupProposalStats,
  getMeetupProposals,
  rejectMeetupProposal,
  updateMeetupProposal,
  type UpdateMeetupProposalPayload,
} from "../api/meetupProposals";
import { meetupEventKeys } from "./useMeetupEvents";
import type { MeetupProposalParams } from "../types/meetupProposal";

export const meetupProposalKeys = {
  all:   ["meetupProposals"] as const,
  list:  (p: MeetupProposalParams) => [...meetupProposalKeys.all, "list", p] as const,
  stats: () => [...meetupProposalKeys.all, "stats"] as const,
};

export const useMeetupProposals = (params: MeetupProposalParams) =>
  useQuery({
    queryKey:        meetupProposalKeys.list(params),
    queryFn:         () => getMeetupProposals(params),
    placeholderData: keepPreviousData,
    staleTime:       30_000,
  });

export const useMeetupProposalStats = () =>
  useQuery({
    queryKey:  meetupProposalKeys.stats(),
    queryFn:   getMeetupProposalStats,
    staleTime: 60_000,
  });

export const useApproveMeetupProposal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, imageUrl }: { id: string; imageUrl?: string }) =>
      approveMeetupProposal(id, imageUrl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetupProposalKeys.all });
      qc.invalidateQueries({ queryKey: meetupEventKeys.all });
    },
  });
};

export const useRejectMeetupProposal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => rejectMeetupProposal(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: meetupProposalKeys.all });
      qc.invalidateQueries({ queryKey: meetupEventKeys.all });
    },
  });
};

export const useDeleteMeetupProposal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMeetupProposal(id),
    onSuccess:  () => qc.invalidateQueries({ queryKey: meetupProposalKeys.all }),
  });
};

/** Admin edit on a pending proposal — server rejects if status ≠ 'pending'. */
export const useUpdateMeetupProposal = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateMeetupProposalPayload }) =>
      updateMeetupProposal(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: meetupProposalKeys.all }),
  });
};
